import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Format API error detail (FastAPI may return an array of {msg} objects)
export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);          // null while loading, {} customer, or false when guest
  const [loading, setLoading] = useState(true);

  // Check current session on mount.
  // CRITICAL: If returning from Google OAuth (session_id in URL fragment),
  // skip the /me check so the AuthCallback handler can exchange the session
  // and set the cookie first. Otherwise we'd 401 and mark the user as guest.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/customers/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/customers/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data.customer);
    return data.customer;
  }, []);

  const register = useCallback(async (email, password, full_name) => {
    const { data } = await axios.post(
      `${API}/customers/register`,
      { email, password, full_name },
      { withCredentials: true }
    );
    setUser(data.customer);
    return data.customer;
  }, []);

  // Google OAuth callback: exchange session_id for our JWT cookie
  const completeGoogleLogin = useCallback(async (sessionId) => {
    const { data } = await axios.post(
      `${API}/customers/google/callback`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(data.customer);
    return data.customer;
  }, []);

  // Start Google OAuth flow — dynamic redirect_url, never hardcoded.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  const startGoogleLogin = useCallback(() => {
    const redirectUrl = window.location.origin + "/compte";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/customers/logout`, {}, { withCredentials: true });
    } catch {
      /* noop */
    }
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, startGoogleLogin, completeGoogleLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
